import { useEffect, useState } from 'react'
// @ts-ignore
import { SelectCallback } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import { v4 } from 'uuid'

import Mana from '@/components/Mana/Mana'
import Spinner from '@/components/Spinner/Spinner'
import Typeahead from '@/components/Typeahead/Typeahead'
import { submitFilters } from '@/store/cardlists/actions'
import { SET_ADDITIONAL_FILTERS } from '@/store/cardlists/actions/types'
import { useActionless, useAppSelector } from '@/store/hooks'
import { DEFAULT_FILTER_VALUE, DEFAULT_FILTER_VALUE_MAP, FilterType } from '@/types/additionalFilters'
import { CardListType } from '@/types/page'

import styles from './AdditionalFilters.module.scss'

type RowProps = {
  filter: FilterType
  hideRemove?: boolean
  onChangeFilter: (e: SelectCallback<HTMLInputElement>) => void
  onChangeValues?: (values: FilterType['values']) => void
  onDelete?: () => void
}

export const Row = ({ filter, hideRemove, onChangeFilter, onChangeValues, onDelete }: RowProps) => {
  const { key, type, values } = filter

  return (
    <div className={styles.row}>
      <Col xs='auto'>
        <Form.Control as='select' onChange={(e) => onChangeFilter(e.target.value)} value={type}>
          <option value=' '>Additional Filters...</option>
          <option value='cardName'>Card Name</option>
          <option value='typeLine'>Type Line</option>
          <option value='colors'>Colors</option>
          <option value='manaValue'>Mana Value</option>
          <option value='power'>Power</option>
          <option value='toughness'>Toughness</option>
          <option value='rarity'>Rarity</option>
          <option value='sets'>Sets</option>
          <option value='tags'>Tags</option>
          <option value='price'>Price</option>
          <option value='salt'>Salt</option>
          <option value='num_decks'>Deck #</option>
          <option value='inclusion'>Inclusion %</option>
          <option value='synergy'>Synergy %</option>
        </Form.Control>
      </Col>
      {values &&
        (hideRemove || (
          <>
            {['cardName', 'typeLine'].includes(type) ? (
              <Col xs='auto'>
                <Form.Control
                  onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                  value={values.value}
                />
              </Col>
            ) : ['inclusion', 'manaValue', 'num_decks', 'power', 'price', 'salt', 'synergy', 'toughness'].includes(
                type,
              ) ? (
              <>
                <Col xs='auto'>
                  <Form.Control
                    as='select'
                    onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                    value={values.operator}
                  >
                    <option value='>'>&gt;</option>
                    <option value='>='>&ge;</option>
                    <option value='==='>=</option>
                    <option value='<'>&lt;</option>
                    <option value='<='>&le;</option>
                    <option value='!=='>&ne;</option>
                  </Form.Control>
                </Col>
                <Col xs='auto'>
                  <Form.Control
                    className={styles.number}
                    onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                    type='number'
                    value={values.value}
                  />
                </Col>
              </>
            ) : type === 'colors' ? (
              <>
                {['w', 'u', 'b', 'r', 'g', 'c'].map((color) => (
                  <Col className={styles.color} key={`${key}-${color}`} xs='auto'>
                    <Form.Check
                      checked={!!values[color as keyof FilterType['values']]}
                      id={`${key}-${color}`}
                      label={<Mana className={styles.mana} color={color as 'w' | 'u' | 'b' | 'r' | 'g' | 'c'} />}
                      onChange={() =>
                        onChangeValues && onChangeValues({ [color]: !values[color as keyof FilterType['values']] })
                      }
                      type='checkbox'
                    />
                  </Col>
                ))}
                <Col xs='auto'>
                  <Form.Control
                    as='select'
                    onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                    value={values.operator}
                  >
                    <option value='EXCLUSIVE ALL'>Exactly these colors</option>
                    <option value='INCLUSIVE ALL'>Including these colors</option>
                    <option value='SOME'>At most these colors</option>
                  </Form.Control>
                </Col>
              </>
            ) : type === 'rarity' ? (
              <Col xs='auto'>
                <Form.Control
                  as='select'
                  onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                  value={values.value}
                >
                  <option value='common'>Common</option>
                  <option value='uncommon'>Uncommon</option>
                  <option value='rare'>Rare</option>
                  <option value='mythic'>Mythic</option>
                </Form.Control>
              </Col>
            ) : type === 'sets' ? (
              <>
                <Form.Control
                  as='select'
                  className='w-auto'
                  onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                  value={values.operator}
                >
                  <option value='INCLUDE'>Include</option>
                  <option value='EXCLUDE'>Exclude</option>
                </Form.Control>
                <Typeahead
                  placeholder='Sets'
                  onChange={(sets) =>
                    onChangeValues &&
                    onChangeValues({
                      sets: (sets as { id: string; code: string; name: string }[]).map(({ code }) => code),
                    })
                  }
                  type='sets'
                />
              </>
            ) : type === 'tags' ? (
              <Typeahead
                placeholder='Tags'
                onChange={(tags) =>
                  onChangeValues &&
                  onChangeValues({
                    value: (tags as { id: string; code: string; name: string }[]).map(({ id }) => id),
                  })
                }
                type='tags'
              />
            ) : null}
          </>
        ))}
      {hideRemove || (
        <Button className='px-0' onClick={onDelete} variant='link'>
          remove
        </Button>
      )}
    </div>
  )
}

type Props = {
  initCardLists: CardListType[]
}

const AdditionalFilters = ({ initCardLists }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const filters = useAppSelector((state) => state.cardlists.additionalFilterRows)
  const [setFilters] = useActionless(SET_ADDITIONAL_FILTERS)

  // Re-submit filters if initial card lists change
  useEffect(() => {
    if (filters.length > 0) submitFilters(filters)
  }, [initCardLists])

  const resetFilters = () => {
    setFilters([])
    submitFilters([])
  }

  const changeFilter = (filter: keyof typeof DEFAULT_FILTER_VALUE_MAP, i: number) => {
    const newValues = DEFAULT_FILTER_VALUE_MAP[filter] || DEFAULT_FILTER_VALUE
    setFilters([
      ...filters.slice(0, i),
      {
        key: v4(),
        type: filter,
        values: newValues,
      },
      ...filters.slice(i + 1),
    ])
  }

  const changeValues = (values: FilterType['values'], i: number) => {
    if (!values) return
    let newValues = values
    if (filters[i].type === 'colors' && values.c !== undefined)
      newValues = { ...newValues, ...DEFAULT_FILTER_VALUE_MAP.colors, c: true, operator: newValues.operator }
    else if (filters[i].type === 'colors' && ['w', 'u', 'b', 'r', 'g'].includes(Object.keys(values)[0])) {
      // Colors handler
      newValues = {
        ...values,
        c: false,
      }
      const { ...colors } = {
        ...filters[i].values,
        ...newValues,
      }
      // Checks colorless if nothing is checked
      if (!Object.values(colors).some((c) => c)) {
        newValues.c = true
      }
    }
    setFilters([
      ...filters.slice(0, i),
      {
        ...filters[i],
        values: { ...filters[i].values, ...newValues },
      },
      ...filters.slice(i + 1),
    ])
  }

  const deleteFilter = (i: number) => {
    setFilters([...filters.slice(0, i), ...filters.slice(i + 1)])
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    await submitFilters(filters)
    setIsLoading(false)
  }

  if (filters.length === 0) return null

  return (
    <div className={styles.container}>
      <div className='d-flex flex-column gap-2 m-2'>
        {filters.map((filter, i) => (
          <Row
            filter={filter}
            key={filter.key}
            onChangeFilter={(e) => changeFilter(e as keyof typeof DEFAULT_FILTER_VALUE_MAP, i)}
            onChangeValues={(values) => changeValues(values, i)}
            onDelete={() => deleteFilter(i)}
          />
        ))}
        {filters.length > 0 && (
          <div className='d-flex justify-content-end'>
            <Button disabled={isLoading} onClick={handleSubmit}>
              {isLoading ? <Spinner /> : 'Submit'}
            </Button>
            <Button disabled={isLoading} onClick={resetFilters} variant='link'>
              reset
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdditionalFilters
